/* eslint-disable @typescript-eslint/no-explicit-any */
import { ValidatorFn, AbstractControl, ValidationErrors, FormGroup, FormArray } from '@angular/forms';
import { CTsDatovyTypVysledekDto, HodnotaVlastnostiVysledekDto } from '@generated/be';
import { parseStringToDate } from '@shared/utils';

export enum OvereniCertifikatuValidationErrors {
  MissingMajitelIdentifikatorCombinationError = 'missingMajitelIdentifikatorCombinationError',
}
export enum OsobaValidationErrors {
  InvalidPhysicalPerson = 'invalidPhysicalPerson',
  InvalidLegalPerson = 'invalidLegalPerson',
  MissingNameDobCombination = 'missingNameDobCombination',
  MissingDocumentCombination = 'missingDocumentCombination',
  MissingRequiredCombination = 'missingRequiredCombination',
  MissingNazevIcoCombination = 'missingNazevIcoCombination',
  DokladTotoznostiIncomplete = 'dokladTotoznostiIncomplete',
}
export enum SablonyValidationErrors {
  NazevOrUdajRequired = 'nazevOrUdajRequired',
  MinInExForbidden = 'minInclExclForbidden',
  MaxInExForbidden = 'maxInclExclForbidden',
  MinMaxError = 'minMaxError',
  MinMaxInclError = 'minMaxInclError',
  MinMaxExclError = 'minMaxExclError',
  TotalDigitsError = 'totalDigitsError',
}
export enum ObjektValidationErrors {
  DuplicateIdentifikator = 'duplicateIdentifikator',
}

export class HtmlValidators {
  static required(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return { required: true };
    }
    const stripped = value
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
    return stripped.length === 0 ? { required: true } : null;
  }
}

export class OsobaValidators {
  static validatePhysicalPersonX(allowedErrors: OsobaValidationErrors[] = []): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      if (!group.get) return null;

      const jmeno = group.get('jmeno')?.value;
      const prijmeni = group.get('prijmeni')?.value;
      const datumNarozeni = group.get('datumNarozeni')?.value;
      const dokladTotoznosti = group.get('dokladTotoznosti');
      const druhDokladu = dokladTotoznosti?.get('typ')?.value;
      const cisloDokladu = dokladTotoznosti?.get('cislo')?.value;

      const isNameDobValid = !!jmeno && !!prijmeni && !!datumNarozeni;
      const isNameDobPartiallyFilled = !!jmeno || !!prijmeni || !!datumNarozeni;

      const isDocumentValid = !!druhDokladu && !!cisloDokladu;
      const isDocumentPartiallyFilled = !!druhDokladu || !!cisloDokladu;

      const errors: ValidationErrors = {};

      const parentGroup = group.parent;
      const isFyzickaOsoba = parentGroup?.get('isFyzickaOsoba')?.value;

      // Pokud jsou všechny hodnoty prázdné, vyvoláme chybu

      // if(const ico = dokladTotoznosti?.get('ico')?.value)

      if (allowedErrors.includes(OsobaValidationErrors.MissingRequiredCombination)) {
        const icoControl = group.get('ico');

        if (icoControl) {
          if (!isFyzickaOsoba) {
            errors[OsobaValidationErrors.MissingRequiredCombination] = isFyzickaOsoba
              ? 'V případě volby fyzické osoby - musí být vyplněna alespoň jedna z těchto  kombinací: (jméno, příjmení a datum narození), nebo (druh dokladu a číslo dokladu).'
              : 'V případě volby právnické osoby / podnikající fyzické osoby - musí být vyplněno IČO.';
          }
        } else {
          if (!jmeno && !prijmeni && !datumNarozeni && !druhDokladu && !cisloDokladu) {
            errors[OsobaValidationErrors.MissingRequiredCombination] =
              'Musí být vyplněna alespoň jedna kombinace: (jméno, příjmení a datum narození), nebo (druh dokladu a číslo dokladu).';
          }
        }
      }

      // Kontrola pro jméno, příjmení, datum narození
      if (isNameDobPartiallyFilled && !isNameDobValid) {
        errors[OsobaValidationErrors.MissingNameDobCombination] =
          'Kombinace jméno, příjmení a datum narození musí být vyplněna současně.';
      }

      if (isNameDobPartiallyFilled) {
        ['jmeno', 'prijmeni', 'datumNarozeni'].forEach((field) => {
          const control = group.get(field);
          if (!control?.value) {
            control?.setErrors({ req: true });
          } else {
            control?.setErrors(null);
          }
        });
      } else {
        ['jmeno', 'prijmeni', 'datumNarozeni'].forEach((field) => {
          const control = group.get(field);
          control?.setErrors(null);
        });
      }

      // Kontrola pro druh dokladu a číslo dokladu
      if (isDocumentPartiallyFilled && !isDocumentValid) {
        errors[OsobaValidationErrors.MissingDocumentCombination] =
          'Kombinace druh dokladu a číslo dokladu musí být vyplněna současně.';
      }

      // Pokud je detekována chyba v jedné z výše uvedených kombinací, tak i obecna InvalidPhysicalPerson
      if (
        errors[OsobaValidationErrors.MissingNameDobCombination] ||
        errors[OsobaValidationErrors.MissingDocumentCombination]
      ) {
        errors[OsobaValidationErrors.InvalidPhysicalPerson] = true;
      }

      // Pokud nejsou žádné chyby, vrátí se null, jinak vrátí chyby
      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  static validatePhysicalPerson(allowedErrors: OsobaValidationErrors[] = []): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      if (!group.get) return null;

      const jmeno = group.get('jmeno')?.value;
      const prijmeni = group.get('prijmeni')?.value;
      const datumNarozeni = group.get('datumNarozeni')?.value;
      const dokladTotoznosti = group.get('dokladTotoznosti');
      const druhDokladu = dokladTotoznosti?.get('typ')?.value;
      const cisloDokladu = dokladTotoznosti?.get('cislo')?.value;

      const isNameDobValid = !!jmeno && !!prijmeni && !!datumNarozeni;
      const isNameDobPartiallyFilled = !!jmeno || !!prijmeni || !!datumNarozeni;

      const isDocumentValid = !!druhDokladu && !!cisloDokladu;
      const isDocumentPartiallyFilled = !!druhDokladu || !!cisloDokladu;

      const errors: ValidationErrors = {};

      // Pokud jsou všechny hodnoty prázdné, vyvoláme chybu

      // if(const ico = dokladTotoznosti?.get('ico')?.value)

      if (allowedErrors.includes(OsobaValidationErrors.MissingRequiredCombination)) {
        const icoControl = group.get('ico');

        if (icoControl) {
          if (!jmeno && !prijmeni && !datumNarozeni && !druhDokladu && !cisloDokladu && !icoControl.value) {
            errors[OsobaValidationErrors.MissingRequiredCombination] =
              `Musí být vyplněna alespoň jedna z těchto možností: (jméno, příjmení a datum narození), (druh dokladu a číslo dokladu) nebo IČO.`;
          }
        } else {
          if (!jmeno && !prijmeni && !datumNarozeni && !druhDokladu && !cisloDokladu) {
            errors[OsobaValidationErrors.MissingRequiredCombination] =
              'Musí být vyplněna alespoň jedna kombinace: (jméno, příjmení a datum narození), nebo (druh dokladu a číslo dokladu).';
          }
        }
      }

      // Kontrola pro jméno, příjmení, datum narození
      if (isNameDobPartiallyFilled && !isNameDobValid) {
        errors[OsobaValidationErrors.MissingNameDobCombination] =
          'Kombinace jméno, příjmení a datum narození musí být vyplněna současně.';
      }

      if (isNameDobPartiallyFilled) {
        ['jmeno', 'prijmeni', 'datumNarozeni'].forEach((field) => {
          const control = group.get(field);
          if (!control?.value) {
            control?.setErrors({ req: true });
          } else {
            control?.setErrors(null);
          }
        });
      } else {
        ['jmeno', 'prijmeni', 'datumNarozeni'].forEach((field) => {
          const control = group.get(field);
          control?.setErrors(null);
        });
      }

      // Kontrola pro druh dokladu a číslo dokladu
      if (isDocumentPartiallyFilled && !isDocumentValid) {
        errors[OsobaValidationErrors.MissingDocumentCombination] =
          'Kombinace druh dokladu a číslo dokladu musí být vyplněna současně.';
      }

      // Pokud je detekována chyba v jedné z výše uvedených kombinací, tak i obecna InvalidPhysicalPerson
      if (
        errors[OsobaValidationErrors.MissingNameDobCombination] ||
        errors[OsobaValidationErrors.MissingDocumentCombination]
      ) {
        errors[OsobaValidationErrors.InvalidPhysicalPerson] = true;
      }

      // Pokud nejsou žádné chyby, vrátí se null, jinak vrátí chyby
      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  static validateLegalPerson(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      if (!group.get) return null;
      const ico = group.get('ico')?.value;
      const nazev = group.get('nazev')?.value;

      const errors: ValidationErrors = {};

      const nazevAndIco = () => {
        if (!nazev) return false;
        if (nazev && ico) return false;
        return true;
      };

      if (nazevAndIco()) {
        errors[OsobaValidationErrors.MissingNazevIcoCombination] = 'V případě vyplnění názvu je nutné vyplnit i IČO.';
      }

      // Pokud je detekována chyba v jedné z výše uvedených kombinací, tak i obecna InvalidLegalPerson
      if (errors[OsobaValidationErrors.MissingNazevIcoCombination]) {
        errors[OsobaValidationErrors.InvalidLegalPerson] = true;
      }

      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  static validateDokladTotoznosti: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
    const cislo = group.get('cislo')?.value;
    const typ = group.get('typ')?.value;

    // Chybu vracíme na úrovni groupy (ne přes setErrors na child controls). Imperativní setErrors
    // na child controls je Angular anti-pattern – při následné re-validaci child controlu Angular
    // chybu smaže, takže se hláška zobrazí jen přechodně. Group-level chyba zůstane stabilní.
    if (cislo && !typ) {
      return {
        [OsobaValidationErrors.DokladTotoznostiIncomplete]: {
          typ: 'V případě vyplnění čísla dokladu je nutné vybrat druh dokladu totožnosti. ',
        },
      };
    }

    if (typ && !cislo) {
      return {
        [OsobaValidationErrors.DokladTotoznostiIncomplete]: {
          cislo: 'V případě vybrání druhu dokladu totožnosti je nutné vyplnit číslo dokladu totožnosti. ',
        },
      };
    }

    return null;
  };

  // Reklamace implicitních oprávnění
  static validateZastupImplicit(selectedOption: 'datumNarozeni' | 'doklad' | 'ico'): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      if (!(group instanceof FormGroup)) return null;

      const errors: ValidationErrors = {};

      const jmeno = group.get('jmeno')?.value;
      const prijmeni = group.get('prijmeni')?.value;
      const datumNarozeni = group.get('datumNarozeni')?.value;
      const dokladGroup = group.get('dokladTotoznosti') as FormGroup;
      const typDokladu = dokladGroup?.get('typ')?.value;
      const cisloDokladu = dokladGroup?.get('cislo')?.value;
      const ico = group.get('ico')?.value;

      const duvod = group.parent?.get('duvod')?.value;

      // --- Důvod always required ---
      const duvodControl = group.parent?.get('duvod');
      if (!duvod || duvod.trim() === '') {
        duvodControl?.setErrors({ required: true });
      } else {
        duvodControl?.setErrors(null);
      }

      switch (selectedOption) {
        case 'datumNarozeni': {
          const nameDobValid = jmeno && prijmeni && datumNarozeni;
          if (!nameDobValid) {
            errors[OsobaValidationErrors.MissingRequiredCombination] =
              'Musí být vyplněna kombinace: Jméno + Příjmení + Datum narození.';
          }
          break;
        }

        case 'doklad': {
          const dokladValid = jmeno && prijmeni && typDokladu && cisloDokladu;
          if (!dokladValid) {
            errors[OsobaValidationErrors.MissingRequiredCombination] =
              'Musí být vyplněna kombinace: Jméno + Příjmení + Druh dokladu + Číslo dokladu.';
          }
          break;
        }

        case 'ico': {
          if (!ico) {
            errors[OsobaValidationErrors.MissingRequiredCombination] = 'IČO je povinné.';
          }
          break;
        }
      }

      return Object.keys(errors).length > 0 ? errors : null;
    };
  }
}

export class SablonyValidators {
  static nazevOrUdajRequired: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
    const formGroup = group as FormGroup;
    const udajRpp = formGroup.get('cUdajRpp')?.value;
    const nazev = formGroup.get('nazev')?.value;

    const errors: ValidationErrors = {};

    if ((udajRpp === null || udajRpp === '') && (nazev === null || nazev === '')) {
      errors[SablonyValidationErrors.NazevOrUdajRequired] = 'Je nutné zadat Název nebo Kód a název údaje dle RPP';
    }

    return Object.keys(errors).length > 0 ? errors : null;
  };

  static hodnotyVlastnosti: ValidatorFn = (groups: AbstractControl): ValidationErrors | null => {
    const formGroups = groups as FormArray;
    if (!formGroups || formGroups.length < 1) return null;

    const datovyTyp = formGroups.at(0).value.datovyTyp as CTsDatovyTypVysledekDto;
    if (!datovyTyp || datovyTyp.kod === 'xs:boolean') return null;

    const errors: ValidationErrors = {};
    const valuesByKod = SablonyValidators.reduceFormArray(formGroups) as any;

    if (datovyTyp.kod === 'xs:int') {
      return this.resolveIntValidation(valuesByKod, errors);
    }

    if (datovyTyp.kod === 'xs:date') {
      return this.resolveDateValidation(valuesByKod, errors);
    }

    if (datovyTyp.kod === 'xs:string') {
      return this.resolveStringValidation(valuesByKod, errors);
    }

    return null;
  };

  private static resolveIntValidation(valuesByKod: any, errors: ValidationErrors): ValidationErrors | null {
    const minIn = valuesByKod['minIncl'];
    const maxIn = valuesByKod['maxIncl'];
    const minEx = valuesByKod['minExcl'];
    const maxEx = valuesByKod['maxExcl'];
    const total = valuesByKod['totalDig'];

    const minInValue = minIn?.hodnota;
    const maxInValue = maxIn?.hodnota;
    const minExValue = minEx?.hodnota;
    const maxExValue = maxEx?.hodnota;
    const totalValue = total?.hodnota;

    if (minIn && minEx) {
      errors[SablonyValidationErrors.MinInExForbidden] =
        'Nelze mít najednou vybrané vlastnosti MinInclusive a MinExclusive';
    }
    if (maxIn && maxEx) {
      errors[SablonyValidationErrors.MaxInExForbidden] =
        'Nelze mít najednou vybrané vlastnosti MaxInclusive a MaxExclusive';
    }
    if (totalValue === 0 || totalValue < 1) {
      errors[SablonyValidationErrors.TotalDigitsError] = 'Počet číslic musí být vyšší než 0';
    }

    if (maxInValue < minInValue) {
      errors[SablonyValidationErrors.MinMaxInclError] =
        'Minimální hodnota včetně nesmí být větší než Maximální hodnota včetně.';
    }
    if (maxExValue <= minInValue || maxInValue <= minExValue) {
      errors[SablonyValidationErrors.MinMaxError] =
        'Minimální hodnota nesmí být větší nebo stejná jako Maximální hodnota.';
    }
    if (maxExValue - 1 <= minExValue) {
      errors[SablonyValidationErrors.MinMaxExclError] =
        'Minimální hodnota výlučně nesmí být větší nebo stejná jako Maximální hodnota výlučně mínus 1.';
    }
    return Object.keys(errors).length > 0 ? errors : null;
  }

  private static resolveDateValidation(valuesByKod: any, errors: ValidationErrors): ValidationErrors | null {
    const minIn = valuesByKod['minIncl'];
    const maxIn = valuesByKod['maxIncl'];
    const minEx = valuesByKod['minExcl'];
    const maxEx = valuesByKod['maxExcl'];

    if (minIn && minEx) {
      errors[SablonyValidationErrors.MinInExForbidden] =
        'Nelze mít najednou vybrané vlastnosti MinInclusive a MinExclusive';
    }
    if (maxIn && maxEx) {
      errors[SablonyValidationErrors.MaxInExForbidden] =
        'Nelze mít najednou vybrané vlastnosti MaxInclusive a MaxExclusive';
    }

    const minInDate =
      minIn && minIn.hodnota && typeof minIn.hodnota === 'string' ? parseStringToDate(minIn.hodnota) : null;
    const maxInDate =
      maxIn && maxIn.hodnota && typeof maxIn.hodnota === 'string' ? parseStringToDate(maxIn.hodnota) : null;
    const minExDate =
      minEx && minEx.hodnota && typeof minEx.hodnota === 'string' ? parseStringToDate(minEx.hodnota) : null;
    const maxExDate =
      maxEx && maxEx.hodnota && typeof maxEx.hodnota === 'string' ? parseStringToDate(maxEx.hodnota) : null;

    if (minInDate && maxInDate && maxInDate.getTime() < minInDate.getTime()) {
      errors[SablonyValidationErrors.MinMaxInclError] =
        'Minimální hodnota včetně nesmí být větší než Maximální hodnota včetně.';
    }
    if (
      (minInDate && maxExDate && maxExDate.getTime() <= minInDate.getTime()) ||
      (minExDate && maxInDate && maxInDate.getTime() <= minExDate.getTime())
    ) {
      errors[SablonyValidationErrors.MinMaxError] =
        'Minimální hodnota nesmí být větší nebo stejná jako Maximální hodnota.';
    }
    if (minExDate && maxExDate && maxExDate.getTime() - 86400000 <= minExDate.getTime()) {
      errors[SablonyValidationErrors.MinMaxExclError] =
        'Minimální hodnota výlučně nesmí být větší nebo stejná jako Maximální hodnota výlučně mínus 1 den.';
    }
    return Object.keys(errors).length > 0 ? errors : null;
  }

  private static resolveStringValidation(valuesByKod: any, errors: ValidationErrors): ValidationErrors | null {
    const min = valuesByKod['minLength'];
    const max = valuesByKod['maxLength'];

    if ((min?.hodnota || min?.hodnota === 0) && (max?.hodnota || max?.hodnota === 0) && min.hodnota > max.hodnota) {
      errors[SablonyValidationErrors.MinMaxError] = 'Minimální hodnota nesmí být větší než Maximální hodnota';
    }
    return Object.keys(errors).length > 0 ? errors : null;
  }

  private static reduceFormArray(formArray: FormArray<FormGroup>) {
    return formArray.controls.reduce(
      (acc, control) => {
        const kod = control.value?.vlastnost?.kod;
        if (kod) {
          acc[kod] = control.value;
        }
        return acc;
      },
      {} as Record<string, HodnotaVlastnostiVysledekDto>
    );
  }
}

export class OvereniCertifikatuValidators {
  static validateOvereniCertifikatu(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control) return null;

      const form = control as FormGroup;

      const majitelValue = form.controls['majitel'].value;
      const majitelValid = form.controls['majitel'].valid;

      const emptyJmeno = !majitelValue.jmeno;
      const emptyCisloDokladu = !majitelValue.dokladTotoznosti?.cislo;
      const emptyIco = !majitelValue.ico;
      const emptyMajitel = emptyJmeno && emptyCisloDokladu && emptyIco;

      const identifikatorValue = form.controls['identifikatorCertifikatu'].value;
      const certifikatValue = form.controls['certifikat'].value;

      const errors: ValidationErrors = {};

      const validMajitelIdentifikator = () => {
        return (!emptyMajitel && majitelValid) || identifikatorValue || certifikatValue;
      };

      if (!validMajitelIdentifikator()) {
        errors[OvereniCertifikatuValidationErrors.MissingMajitelIdentifikatorCombinationError] =
          'Musí být vyplněny údaje o specifikaci certifikátu nebo jeho majiteli.';
      }

      return Object.keys(errors).length > 0 ? errors : null;
    };
  }
}

export class ObjektValidators {
  static duplicateIdentifikator(
    existingObjekty: { nazev?: { kod?: string }; identifikator?: { kod?: string } }[],
    getNazevKod: () => string | undefined
  ): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      if (!(group instanceof FormGroup)) return null;

      const meta = group.get('meta')?.value;
      const nazevKod = getNazevKod();
      const identifikatorControl = group.get('identifikator');

      if (!meta?.kod || !nazevKod || !identifikatorControl) {
        return null;
      }

      const isDuplicate = existingObjekty.some((o) => o.nazev?.kod === nazevKod && o.identifikator?.kod === meta.kod);

      if (isDuplicate) {
        identifikatorControl.setErrors({
          [ObjektValidationErrors.DuplicateIdentifikator]:
            'Objekt oprávnění s daným identifikátorem byl již přidán, prosím odeberte tento identifikátor.',
        });
      } else {
        if (identifikatorControl.hasError(ObjektValidationErrors.DuplicateIdentifikator)) {
          const errors = { ...identifikatorControl.errors };
          delete errors[ObjektValidationErrors.DuplicateIdentifikator];
          identifikatorControl.setErrors(Object.keys(errors).length ? errors : null);
        }
      }

      return null;
    };
  }
}
